import { useCallback, useRef } from "react";

import { APP_ACTIONS } from "./actionTypes";
import { buildWholeAppWorldState, recordWholeAppJourneyEvent } from "../world-model/appJourney.js";
import { normalizeLivePortal, recordLiveAppInteraction } from "../world-model/liveAppPerception";

export function triggerControlledMeetJozFlex(runtimeWindow = typeof window !== "undefined" ? window : null) {
  if (typeof runtimeWindow?.__triggerControlledGLB !== "function") return false;
  return runtimeWindow.__triggerControlledGLB("vibe") === true;
}

export function triggerControlledMeetJozSkills(runtimeWindow = typeof window !== "undefined" ? window : null) {
  if (typeof runtimeWindow?.__triggerControlledGLB !== "function") return false;
  return runtimeWindow.__triggerControlledGLB("skills") === true;
}

export function dispatchCameraCommand(
  payload = {},
  runtimeWindow = typeof window !== "undefined" ? window : null
) {
  const direction = String(payload.direction || "").toLowerCase().trim();
  if (!runtimeWindow?.dispatchEvent || !["right", "left", "up", "down"].includes(direction)) {
    return false;
  }

  runtimeWindow.dispatchEvent(new CustomEvent("world-camera-command", {
    detail: {
      direction,
      degrees: Number.isFinite(Number(payload.degrees)) ? Number(payload.degrees) : 42,
      source: payload.source || "joz-maxx",
    },
  }));
  return true;
}

export function useAppActionDispatcher({
  announcePortalTransition,
  setLocation,
  handleBallPortalOpen,
  showContactCta,
  hideContactCta,
  setPendingMeetJozVoiceAction,
  pendingPortalActionRef,
  handleVibeClick,
  handleBackClick,
  handleDiscoverClick,
  handleBack1Click,
  handleSkillsClick,
}) {
  const skillsDispatchActiveRef = useRef(false);

  return useCallback(
    (type, payload = {}) => {
      const recordAction = (action, target = null, portal = null) => {
        recordLiveAppInteraction(action, target, { portal });
        void recordWholeAppJourneyEvent({
          action,
          target,
          source: "app_action_dispatcher",
          outcomeType: "action_accepted",
        }).catch((error) => {
          console.warn("⚠️ Whole-app journey recording failed:", error?.message || error);
        });
      };

      switch (type) {
        case APP_ACTIONS.SHOW_CONTACT:
          showContactCta(payload);
          recordAction("show_contact", "contact");
          return true;

        case APP_ACTIONS.HIDE_CONTACT:
          hideContactCta(payload.delayMs);
          recordAction("hide_contact", "contact");
          return true;

        case APP_ACTIONS.ROTATE_CAMERA: {
          const handled = dispatchCameraCommand(payload);
          if (handled) recordAction("rotate_camera", payload.direction);
          return handled;
        }

        case APP_ACTIONS.OPEN_BALL_PORTAL:
          handleBallPortalOpen?.();
          recordAction("open_ball_portal", "ball");
          return true;

        case APP_ACTIONS.NAVIGATE: {
          const targetPath = String(payload.targetPath || "").trim();
          if (!targetPath.startsWith("/")) return false;
          const currentAppState = typeof window !== "undefined" ? window.__appState || {} : {};
          const sourcePath = currentAppState.currentPath || (typeof window !== "undefined" ? window.location.pathname : "/");
          const sourcePortal = normalizeLivePortal(
            String(sourcePath).match(/^\/neo\/([^/]+)/i)?.[1] || currentAppState.currentPortal || "root"
          );
          const targetPortal = normalizeLivePortal(
            String(targetPath).match(/^\/neo\/([^/]+)/i)?.[1] || "root"
          );
          const previousHistory = typeof window !== "undefined" && Array.isArray(window.__liveAppContext?.portal_history)
            ? window.__liveAppContext.portal_history
            : [sourcePortal];
          const transitionHistory = [
            ...previousHistory,
            targetPortal,
          ].slice(-48);
          const transitionBefore = buildWholeAppWorldState({
            appState: { ...currentAppState, currentPath: sourcePath, currentPortal: sourcePortal },
            overrides: { portalHistory: previousHistory },
          });
          const transitionAfter = buildWholeAppWorldState({
            appState: { ...currentAppState, currentPath: targetPath, currentPortal: targetPortal },
            overrides: { portalHistory: transitionHistory },
          });

          const deferredAction = String(payload.deferredAction || "").trim();
          if (
            pendingPortalActionRef &&
            targetPath === "/neo/meet-joz" &&
            ["vibe", "discover", "skills"].includes(deferredAction)
          ) {
            pendingPortalActionRef.current = {
              action: deferredAction,
              runNuclearSkillsSequence: Boolean(payload.runNuclearSkillsSequence),
              requestedAt: Date.now(),
              sourcePortal:
                typeof window !== "undefined"
                  ? window.location.pathname
                  : "",
            };
            setPendingMeetJozVoiceAction?.(deferredAction);
          }

          announcePortalTransition(targetPath);
          setLocation(targetPath);
          recordAction("navigate", targetPath, targetPortal);
          if (sourcePortal !== targetPortal) {
            void recordWholeAppJourneyEvent({
              action: "portal_transition",
              target: targetPath,
              source: "app_action_dispatcher",
              outcomeType: "portal_transition_observed",
              stateBefore: transitionBefore,
              observedState: transitionAfter,
              expectedEffects: [{ type: "portal_transition", from: sourcePortal, to: targetPortal }],
              observedEffects: [{ type: "portal_transition", from: sourcePortal, to: targetPortal }],
            }).catch((error) => {
              console.warn("⚠️ Portal transition recording failed:", error?.message || error);
            });
          }
          return true;
        }

        case APP_ACTIONS.MEET_JOZ_FLEX:
          handleVibeClick?.();
          recordAction("meet_joz_vibe", "vibe");
          return true;

        case APP_ACTIONS.MEET_JOZ_TRIGGER_FLEX: {
          const triggered = triggerControlledMeetJozFlex();
          if (triggered) recordAction("meet_joz_vibe_trigger", "vibe");
          return triggered;
        }

        case APP_ACTIONS.MEET_JOZ_DISCOVER:
          handleDiscoverClick?.();
          recordAction("meet_joz_discover", "discover");
          return true;

        case APP_ACTIONS.MEET_JOZ_SKILLS: {
          if (skillsDispatchActiveRef.current) {
            // ControlledGLB calls the Skills handler when its animation has
            // already reached the Skills stop. Calling the controlled trigger
            // again here would recursively restart the same transition.
            handleSkillsClick?.();
          } else {
            skillsDispatchActiveRef.current = true;
            try {
              const triggered = triggerControlledMeetJozSkills();
              if (!triggered) handleSkillsClick?.();
            } finally {
              skillsDispatchActiveRef.current = false;
            }
          }
          recordAction("meet_joz_skills", "skills");
          return true;
        }

        case APP_ACTIONS.MEET_JOZ_BACK:
          handleBackClick?.();
          recordAction("meet_joz_back", "back");
          return true;

        case APP_ACTIONS.MEET_JOZ_BACK1:
          handleBack1Click?.();
          recordAction("meet_joz_back1", "back");
          return true;

        default:
          return false;
      }
    },
    [
      announcePortalTransition,
      handleBallPortalOpen,
      handleBack1Click,
      handleBackClick,
      handleDiscoverClick,
      handleSkillsClick,
      handleVibeClick,
      hideContactCta,
      pendingPortalActionRef,
      setLocation,
      setPendingMeetJozVoiceAction,
      showContactCta,
    ]
  );
}
